// src/adminPages/Dashboard.js
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { getItemsbyType } from "../service/APIService";
import "../styles/AdminDashboard.css";

// Every record type that belongs to a user, including removed apps whose rows still exist.
const USER_DATA_TYPES = [
  "expense",
  "expense-category",
  "todo",
  "todocategory",
  "contactus",
  "weightlist",
  "splitequal-friends",
  "splitequal-expense",
  "usersfriend",
];

// Records have no real "updated at" field, so use their created/entry date.
const toTime = (value) => {
  if (!value) return 0;
  const text = String(value);
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const t = dateOnly
    ? new Date(dateOnly[1], dateOnly[2] - 1, dateOnly[3]).getTime()
    : new Date(text.replace(" ", "T")).getTime();
  return isNaN(t) ? 0 : t;
};

const formatDate = (t) =>
  t ? new Date(t).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "—";

const formatRelativeTime = (t) => {
  if (!t) return "No activity";
  const diffDays = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(t);
};

const asList = (data) => (Array.isArray(data) ? data : []);

const Dashboard = () => {
  const [userCount, setUserCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [inprogressCount, setInProgressCount] = useState(0);
  const [totalRecordsCount, setTotalRecordsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userRows, setUserRows] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [usersData, ...rowsByType] = await Promise.all([
        getItemsbyType("appusers"),
        ...USER_DATA_TYPES.map((type) => getItemsbyType(type).catch(() => [])),
      ]);

      const users = asList(usersData);
      setUserCount(users.length);

      const allRows = rowsByType.map(asList).flat();
      setTotalRecordsCount(allRows.length);

      // Row count and latest date per user email
      const stats = {};
      allRows.forEach((row) => {
        const key = (row.email || "").toLowerCase();
        if (!stats[key]) stats[key] = { rows: 0, expenses: 0, todos: 0, last: 0 };
        stats[key].rows += 1;
        if (row.type === "expense") stats[key].expenses += 1;
        if (row.type === "todo") stats[key].todos += 1;
        stats[key].last = Math.max(stats[key].last, toTime(row.createddate || row.date));
      });

      const rows = users
        .map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          ...(stats[(user.email || "").toLowerCase()] || { rows: 0, expenses: 0, todos: 0, last: 0 }),
        }))
        .sort((a, b) => b.last - a.last || b.rows - a.rows);

      setUserRows(rows);
      setLastUpdated(Math.max(0, ...rows.map((row) => row.last)));

      const contactCases = asList(rowsByType[USER_DATA_TYPES.indexOf("contactus")]);
      const pending = contactCases.filter((caseItem) => (caseItem.status || "").toUpperCase() === "PENDING").length;
      const completed = contactCases.filter((caseItem) => (caseItem.status || "").toUpperCase() === "COMPLETED").length;
      const inprogress = contactCases.filter((caseItem) => (caseItem.status || "").toUpperCase() === "INPROGRESS").length;

      setPendingCount(pending);
      setCompletedCount(completed);
      setInProgressCount(inprogress);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const totalTickets = pendingCount + completedCount + inprogressCount;
  const pendingPct = totalTickets > 0 ? (pendingCount / totalTickets) * 100 : 0;
  const inprogressPct = totalTickets > 0 ? (inprogressCount / totalTickets) * 100 : 0;
  const completedPct = totalTickets > 0 ? (completedCount / totalTickets) * 100 : 0;

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return userRows;
    const q = searchTerm.toLowerCase();
    return userRows.filter(
      (u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );
  }, [userRows, searchTerm]);

  return (
    <div className="admin-container">
      {/* Hero Platform Analytics Card */}
      <div className="admin-hero-card">
        <div className="admin-hero-top">
          <span className="admin-hero-badge">
            <i className="fi fi-rr-shield-check"></i> Admin Console
          </span>

          <button
            type="button"
            className="admin-hero-refresh-btn"
            onClick={fetchDashboardData}
            title="Refresh Data"
            disabled={loading}
          >
            <i className={`fi fi-rr-refresh ${loading ? "fa-spin" : ""}`}></i>
          </button>
        </div>

        <div className="admin-hero-stat-large">{userCount}</div>
        <div className="admin-hero-stat-desc">Active Registered Accounts</div>

        <div className="admin-hero-footer">
          <span>
            <i className="fi fi-rr-database me-1"></i> {totalRecordsCount} Total Platform Records
          </span>
          <span>
            Updated: {formatDate(lastUpdated)}
          </span>
        </div>
      </div>

      {/* 2x2 KPI Grid */}
      <div className="admin-kpi-grid">
        {/* KPI 1: Users */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-top">
            <div className="admin-kpi-icon-wrap admin-kpi-icon-users">
              <i className="fi fi-rr-users-alt"></i>
            </div>
            <span className="admin-pill-tag admin-pill-todo">Active</span>
          </div>
          <div className="admin-kpi-amount">{userCount}</div>
          <div className="admin-kpi-label">Registered Users</div>
        </div>

        {/* KPI 2: Pending Cases */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-top">
            <div className="admin-kpi-icon-wrap admin-kpi-icon-pending">
              <i className="fi fi-rr-clock"></i>
            </div>
            <span className="admin-pill-tag admin-pill-expense">Action</span>
          </div>
          <div className="admin-kpi-amount" style={{ color: "var(--accent-coral)" }}>
            {pendingCount}
          </div>
          <div className="admin-kpi-label">Pending Cases</div>
        </div>

        {/* KPI 3: In Progress */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-top">
            <div className="admin-kpi-icon-wrap admin-kpi-icon-progress">
              <i className="fi fi-rr-refresh"></i>
            </div>
            <span className="admin-pill-tag" style={{ background: "#EFF6FF", color: "#3B82F6" }}>
              Active
            </span>
          </div>
          <div className="admin-kpi-amount" style={{ color: "#3B82F6" }}>
            {inprogressCount}
          </div>
          <div className="admin-kpi-label">In Progress</div>
        </div>

        {/* KPI 4: Resolved */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-top">
            <div className="admin-kpi-icon-wrap admin-kpi-icon-completed">
              <i className="fi fi-rr-check-circle"></i>
            </div>
            <span className="admin-pill-tag" style={{ background: "#ECFDF5", color: "#10B981" }}>
              Resolved
            </span>
          </div>
          <div className="admin-kpi-amount" style={{ color: "#10B981" }}>
            {completedCount}
          </div>
          <div className="admin-kpi-label">Completed</div>
        </div>
      </div>

      {/* Support Funnel Bar */}
      {totalTickets > 0 && (
        <div className="admin-funnel-card">
          <div className="d-flex justify-content-between align-items-center">
            <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--primary-dark)" }}>
              Support Tickets Pipeline ({totalTickets})
            </span>
            <Link
              to="/admin/contactus"
              style={{ fontSize: "12px", fontWeight: 600, color: "var(--accent-coral)", textDecoration: "none" }}
            >
              Manage &rarr;
            </Link>
          </div>

          <div className="admin-funnel-bar-track">
            {completedPct > 0 && (
              <div
                className="admin-funnel-segment"
                style={{ width: `${completedPct}%`, backgroundColor: "#10B981" }}
                title={`Completed: ${completedCount}`}
              />
            )}
            {inprogressPct > 0 && (
              <div
                className="admin-funnel-segment"
                style={{ width: `${inprogressPct}%`, backgroundColor: "#3B82F6" }}
                title={`In Progress: ${inprogressCount}`}
              />
            )}
            {pendingPct > 0 && (
              <div
                className="admin-funnel-segment"
                style={{ width: `${pendingPct}%`, backgroundColor: "var(--accent-coral)" }}
                title={`Pending: ${pendingCount}`}
              />
            )}
          </div>

          <div className="admin-funnel-legend">
            <span>
              <span style={{ color: "#10B981" }}>●</span> Resolved {completedCount}
            </span>
            <span>
              <span style={{ color: "#3B82F6" }}>●</span> Working {inprogressCount}
            </span>
            <span>
              <span style={{ color: "var(--accent-coral)" }}>●</span> Pending {pendingCount}
            </span>
          </div>
        </div>
      )}

      {/* Users Directory Card */}
      <div className="admin-users-card">
        <div className="admin-users-header">
          <div>
            <h6 className="mb-0 fw-bold" style={{ color: "var(--primary-dark)" }}>
              Registered Users
            </h6>
            <small className="text-muted" style={{ fontSize: "11px" }}>
              Sorted by recency of activity
            </small>
          </div>
          <span
            className="badge"
            style={{
              background: "var(--accent-mauve-soft)",
              color: "var(--primary-deep)",
              borderRadius: "12px",
              padding: "5px 10px",
              fontSize: "11px",
            }}
          >
            {userRows.length} users
          </span>
        </div>

        {/* Search Bar */}
        <div className="admin-search-wrap position-relative">
          <i
            className="fi fi-rr-search position-absolute text-muted"
            style={{ left: "30px", top: "18px", fontSize: "13px" }}
          ></i>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* User Rows */}
        {loading ? (
          <div className="p-4 text-center text-muted">
            <i className="fas fa-spinner fa-spin me-2" style={{ color: "var(--primary-deep)" }}></i>
            Loading users data...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-4 text-center text-muted" style={{ fontSize: "13px" }}>
            No matching users found.
          </div>
        ) : (
          filteredUsers.map((user) => {
            const initial = user.name ? user.name.charAt(0).toUpperCase() : (user.email || "U").charAt(0).toUpperCase();
            return (
              <div key={user.id} className="admin-user-row">
                <div className="admin-user-left">
                  <div className="admin-user-avatar">
                    <span>{initial}</span>
                  </div>
                  <div className="admin-user-info">
                    <span className="admin-user-name">{user.name || "Anonymous User"}</span>
                    <span className="admin-user-email">{user.email}</span>
                  </div>
                </div>

                <div className="admin-user-right">
                  <div className="admin-user-pills">
                    <span className="admin-pill-tag admin-pill-expense" title="Expense records">
                      {user.expenses} exp
                    </span>
                    <span className="admin-pill-tag admin-pill-todo" title="To-Do records">
                      {user.todos} todo
                    </span>
                  </div>
                  <span className="admin-user-date" title={formatDate(user.last)}>
                    {formatRelativeTime(user.last)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Dashboard;
